document.addEventListener('alpine:init', () => {
  Alpine.data('ProductForm', () => ({
    errorMessage: null,

    async addToCart() {
      const formData = new FormData(this.$refs.productForm);
      const quantity = Number(formData.get('quantity'));
      const sections = ['cart-drawer', 'cart-footer'];
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) {
          this.errorMessage = 'Failed to add to cart';
          window.dispatchEvent(
            new CustomEvent('cart:error', { detail: 'Failed to add to cart' })
          );
        }

        const data = await res.json();

        if (data.status === 422) {
          this.errorMessage = data.message;
          window.dispatchEvent(
            new CustomEvent('cart:error', { detail: data.message })
          );
          throw new Error(data.message);
        } else {
          // Optimistic UI: bump quantity immediately
          this.$store.cart.incrementQty(quantity);

          // Single source-of-truth event for any cart mutation
          window.dispatchEvent(
            new CustomEvent('cart:update', {
              detail: {
                ...data,
                delta: {
                  type: 'add',
                  key: data.key,
                  qty: quantity,
                },
              },
            })
          );
        }
      } catch (error) {
        this.errorMessage = error.message || 'Failed to add to cart';
        window.dispatchEvent(
          new CustomEvent('cart:error', { detail: error.message })
        );
      }
    },
  }));

  Alpine.data('CartItems', () => ({
    root: null,
    loading: false,

    init() {
      this.root = this.$root;

      window.addEventListener(
        'cart:update',
        this.onCartQuantityChange.bind(this)
      );

      document.addEventListener('cart:lock', () => {
        this.loading = true;
      });

      document.addEventListener('cart:unlock', () => {
        this.loading = false;
      });
    },

    onCartQuantityChange(e) {
      const htmlString = e.detail.sections['cart-drawer'];
      const itemCount = e.detail.item_count;
      const cartItems = getQuerySelectorInHtmlString(
        htmlString,
        '[data-cart-items]'
      );

      if (cartItems) {
        Alpine.morph(this.root, cartItems.outerHTML);
      }

      // Bring input values in sync with DOM updates
      this.$nextTick(() => {
        this.root.querySelectorAll('input').forEach((inputEl) => {
          (inputEl.value = inputEl.getAttribute('value')),
            inputEl.dispatchEvent(new Event('input'));
        });
      });

      if (itemCount !== undefined) {
        this.$store.cart.updateQty(itemCount);
      }
    },
  }));

  Alpine.data('CartItem', (key) => ({
    id: null,
    locked: false,
    quantity: null,
    previousQuantity: null,
    key,
    editingSize: false,
    errorMessage: null,

    init() {
      this.id = Number(key.split(':')[0]);

      document.addEventListener('cart:lock', () => {
        this.locked = true;
      });

      document.addEventListener('cart:unlock', () => {
        this.locked = false;
      });

      // Grab starting quantity from the DOM
      const input = this.$el.querySelector('input[name="updates[]"]');
      if (input) this.quantity = Number(input.value);
    },

    async updateCartItemSize(newVariant) {
      if (this.locked || this.loading) return;

      if (newVariant === this.id) {
        this.editingSize = false;
        return;
      }

      this.locked = true;
      this.loading = true;
      document.dispatchEvent(new CustomEvent('cart:lock'));

      try {
        // 1. Remove current variant
        const removeBody = {
          id: this.key,
          quantity: 0,
          sections: ['cart-drawer', 'cart-footer'],
          sections_url: window.location.pathname,
        };

        const removeRes = await fetch(
          window.Shopify.routes.root + 'cart/change.js',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(removeBody),
          }
        );

        const removeData = await removeRes.json();

        if (removeData.status === 422) {
          this.errorMessage = removeData.message;
          throw new Error(removeData.message || 'Failed to remove variant');
        }

        this.errorMessage = null;

        // 2. Add the new variant
        const formData = new FormData();
        formData.append('id', newVariant);
        formData.append('quantity', this.quantity);
        formData.append('sections', ['cart-drawer', 'cart-footer']);
        formData.append('sections_url', window.location.pathname);

        const addRes = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData,
        });

        const addData = await addRes.json();

        if (addData.status === 422) {
          this.errorMessage = addData.message;
          throw new Error(addData.message || 'Failed to add new variant');
        }

        this.errorMessage = null;

        // Update internal key to new line-item key so future quantity changes work
        this.key = addData.key;

        // Fire unified cart:update event with final addData payload
        window.dispatchEvent(
          new CustomEvent('cart:update', {
            detail: {
              ...addData,
              delta: {
                type: 'change-size',
                fromKey: this.key,
                toVariant: newVariant,
                qty: this.quantity,
              },
            },
          })
        );

        this.editingSize = false;
      } catch (err) {
        console.error(err);
        this.errorMessage = err.message || 'Failed to change size';
        window.dispatchEvent(
          new CustomEvent('cart:error', {
            detail: err.message || 'Failed to change size',
          })
        );
      } finally {
        this.locked = false;
        this.loading = false;
        document.dispatchEvent(new CustomEvent('cart:unlock'));
      }
    },

    async updateCartItemQuantity(newQty) {
      if (this.locked || this.loading) return;

      // Prevent unnecessary requests when quantity hasn't changed
      if (newQty === this.quantity) return;

      this.previousQuantity = this.quantity;
      this.quantity = Number(newQty);

      this.locked = true;
      this.loading = true;

      const body = {
        updates: {
          [this.key]: this.quantity,
        },
        sections: ['cart-drawer', 'cart-footer'],
        sections_url: window.location.pathname,
      };

      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          this.errorMessage = 'Failed to update cart item quantity';
          window.dispatchEvent(
            new CustomEvent('cart:error', {
              detail: 'Failed to update cart item quantity',
            })
          );
        }

        const data = await res.json();

        if (data.status === 422) {
          this.errorMessage = data.message;
          this.quantity = this.previousQuantity;

          window.dispatchEvent(
            new CustomEvent('cart:error:line-item', { detail: data.message })
          );
        } else {
          this.errorMessage = null;

          window.dispatchEvent(
            new CustomEvent('cart:update', {
              detail: {
                ...data,
                delta: {
                  type: 'change',
                  key: this.key,
                  qty: this.quantity,
                  prev: this.previousQuantity,
                },
              },
            })
          );
        }
      } catch (error) {
        console.error(error);
        this.errorMessage = 'Failed to update cart item quantity';
        window.dispatchEvent(
          new CustomEvent('cart:error:unexpected', { detail: error.message })
        );
      } finally {
        this.locked = false;
        this.loading = false;
      }
    },

    removeCartItem() {
      this.updateCartItemQuantity(0);
    },
  }));

  Alpine.data('CartFooter', () => ({
    root: null,

    _morphFooter(e) {
      const footer = getQuerySelectorInHtmlString(
        e.detail.sections['cart-footer'],
        '[data-cart-footer]'
      );

      if (footer) {
        Alpine.morph(this.root, footer.outerHTML);
      }
    },

    init() {
      this.root = this.$root;

      window.addEventListener('cart:update', this._morphFooter.bind(this));
    },
  }));

  Alpine.data('CartSnackBar', () => ({
    show: false,

    init() {
      window.addEventListener('cart:update', this._showSnackBar.bind(this));
    },

    _showSnackBar(e) {
      if (e.detail.delta.type !== 'add') return;

      this.show = true;

      setTimeout(() => {
        this.show = false;
      }, 3000);
    },

    openCart() {
      const cartDrawer = document.getElementById('cart-drawer');
      if (cartDrawer) {
        cartDrawer.open = true;
      }
    },
  }));
});
